<template>
  <div
    id="vuethesearch"
    ref="TheSearchAI"
    :class="bemBlockMod({
      open: openModal,
    })"
  >
    <component
      :is="'style'"
      v-if="styles"
      v-html="styles"
    />
    <span
      v-if="showInputSpan"
      :class="bemElement('input')"
      @click.prevent="show"
    >
      <vue-typer
        v-if="vueTyperData && vueTyperData.text"
        v-bind="vueTyperData"
      />
    </span>
    <BaseImageSearch
      v-if="imageSearchEnabled && showInputSpan"
      :class="bemElement('image-search')"
    />
    <BaseModal
      bem="the-search-ai-modal"
      :is-open="openModal"
      :modal-modifier="'fullscreen'"
      :show-backdrop="true"
      @closed="hide"
    >
      <BasePageRender
        v-if="openModal"
        :page-id="pageId"
        :search-a-i-data="searchAIData"
        :query-parameters="searchQuery ? {
          searchQuery,
        } : {}"
        :search-prop="{
          defaults: {
            hidden: false,
            interacting: false,
            infiniteScrollWrapper: '.the-search-ai-modal__body',
            value: '',
          },
          searchSource,
        }"
        @on-loaded="handlerAnalytics"
      />
    </BaseModal>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import { VueTyper } from 'vue-typer';
import { mapGetters, mapState } from 'vuex';
import { getters, types } from '../store';
import BaseModal from './BaseModal.vue';
import BasePageRender from './BasePageRender.vue';
import BaseImageSearch from './BaseImageSearch.vue';
import bemClassesMixin from '../mixins/bemClassesMixin';
import getFrontConfig from '../../common/getFrontConfig';
import { urlWithSearchParams } from '../../common/utils/url';
import { beginTracking } from '../../analytics/tracking/pageDesignerSections';

const {
  urls: {
    getSerializePage,
  },
  searchAI: {
    alwaysShowsModalOpen,
    isImageSearchEnabled,
    searchModalId,
  },
} = getFrontConfig();

const { createTooltip } = require('../../components/tooltips');

export default {
  name: 'TheSearchAI',

  components: {
    BaseModal,
    BasePageRender,
    BaseImageSearch,
    VueTyper,
  },

  mixins: [
    bemClassesMixin,
  ],

  props: {
    autocompleteSource: {
      type: String,
      default: '/suggest',
    },
    searchSource: {
      type: String,
      default: '/search',
    },
    showInputSpan: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      currentQuery: null,
      pageId: searchModalId,
      toolTipSearchAI: {},
      searchAIData: {},
      imageSearchEnabled: isImageSearchEnabled,
      styles: null,
    };
  },

  computed: {
    ...mapGetters({
      searchQuery: getters.search.lastQuery,
    }),
    ...mapState({
      results: (state) => state.search.results,
      openModal: (state) => state.search.isModalOpen,
      isLoadingSearch: (state) => state.search.isLoading,
    }),

    vueTyperData() {
      const {
        text = null,
        repeat = Infinity,
        shuffle = false,
        initialAction = 'typing',
        preTypeDelay = 250,
        typeDelay = 25,
        preEraseDelay = 2000,
        eraseDelay = 25,
        eraseStyle = 'backspace',
        eraseOnComplete = true,
        caretAnimation = 'blink',
      } = this.searchAIData?.data || {};

      return {
        text: text?.split('\n'),
        repeat,
        shuffle,
        initialAction,
        preTypeDelay,
        typeDelay,
        preEraseDelay,
        eraseDelay,
        eraseStyle,
        eraseOnComplete,
        caretAnimation,
      };
    },
  },

  watch: {
    openModal(value) {
      this.calculateHeaderHeight();
      if (this.toolTipSearchAI?.selector) this.toolTipSearchAI?.closeEvent();
      if (value) {
        this.$eventBus.$on('header-popup:hide', this.hide);
        this.$eventBus.$on('base-product-tile:current-url', this.hide);
      } else {
        this.$eventBus.$off('header-popup:hide', this.hide);
        this.$eventBus.$off('base-product-tile:current-url', this.hide);
      }
    },
  },

  async mounted() {
    this.calculateHeaderHeight();
    await this.getSearchAIData();
    this.showTooltip();
  },

  methods: {
    calculateHeaderHeight() {
      const header = document.querySelector('.header-group');
      document.documentElement.style.setProperty('--header-total-height-with-search', `${header.offsetHeight}px`);
    },

    async getSearchAIData() {
      this.currentQuery = this.searchQuery;
      const parameters = {
        id: this.pageId,
        searchQuery: this.currentQuery,
      };
      const url = urlWithSearchParams(getSerializePage, parameters);
      const searchAIData = await fetch(url)
        .then((response) => response.json());
      if (searchAIData) this.searchAIData = searchAIData;
    },

    /**
     * Appends message, shows alert and enables button
     */
    showTooltip() {
      if (alwaysShowsModalOpen || !this.showInputSpan) return;

      const {
        data: {
          tooltipEnabled = false,
          tooltipTitle,
          tooltipMessage,
          tooltipBtnText,
          tooltipTimeToShow = 3000,
          tooltipBgColor,
          tooltipTextColor,
          tooltipCloseBtnColor,
        },
        custom: {
          data: {
            tooltipAnimationClassName,
            tooltipAnimationEnabled,
            tooltipAnimationEnabledDesktop,
            tooltipAnimationStyles,
          },
        },
      } = this.searchAIData || {};

      if (!tooltipEnabled) return;
      const options = {
        type: 'the-search-ai',
        showCloseBtn: true,
        title: tooltipTitle,
        text: tooltipMessage,
        button: tooltipBtnText,
        color: {
          background: tooltipBgColor,
          text: tooltipTextColor,
          closeBtn: tooltipCloseBtnColor,
        },
        position: 'the-search-ai',
        saveSession: false,
        cookieExpiredType: true,
        cookieAge: 7776000,
      };

      this.toolTipSearchAI = createTooltip(options);
      if (!this.toolTipSearchAI) return;
      this.$refs.TheSearchAI?.appendChild(this.toolTipSearchAI.selector);

      if (tooltipAnimationEnabled || tooltipAnimationEnabledDesktop) {
        this.toolTipSearchAI.selector?.classList.add(
          'animation-image-frontface',
          tooltipAnimationClassName,
        );
        this.styles = tooltipAnimationStyles;
      }

      // eslint-disable-next-line listeners/no-missing-remove-event-listener, listeners/no-inline-function-event-listener
      this.toolTipSearchAI?.buttonElement?.addEventListener('click', () => {
        this.show();
      });

      setTimeout(
        this.toolTipSearchAI.show,
        tooltipTimeToShow,
      );
    },

    hide() {
      this.$store.commit(types.search.setOpenModal, false);
      if (this.searchQuery && this.currentQuery !== this.searchQuery) this.getSearchAIData();
      // TODO: Analytics
    },

    show() {
      this.$store.commit(types.search.setOpenModal, true);
    },

    toggle() {
      if (!this.openModal) {
        this.show();
      } else {
        this.hide();
      }
    },

    handlerAnalytics(params) {
      beginTracking(params);
    },
  },
};
</script>
